import { FeedItem, LivefireApi, EnvironmentHelper } from '@bblabs/module-library/classes';
import Debug from 'debug';
import type { AuthenticatedApiService } from '../services/AuthenticatedApi';

export type NewItem = FeedItem & {
  itemType: 'insert_video' | 'video_saved' | string;
};

export type VideoMetadata = {
  alt_text: string;
  animated_thumb_url: string;
  animated: number;
  duration: number;
  encoder_key: string;
  flashPlayerVidUrl: string;
  full_url: string;
  heatmap_do_not_proc: number;
  height: number;
  hosting_partner: string;
  id: string;
  name: string;
  no_logo: number;
  short_url: string;
  SSLFlashPlayerVidUrl: string;
  thumb_id: string;
  thumb_url_orig: string;
  thumb_url: string;
  true_thumbnail_url: string;
  user_id: string;
  using_custom_thumb: number;
  video_urls: Record<string, string>;
  width: number;
};

type VideoMetadataResponse = {
  info: VideoMetadata;
  methodName: 'GetVideoMetaData';
  status: string;
};

/**
 * Outlook specific Livefire events feed
 */
export class OutlookLivefireApi extends LivefireApi {
  protected authApi: AuthenticatedApiService | null = null;
  private debug = Debug('o365:OutlookLivefireApi');

  constructor(args = {}) {
    super({ ...args });
  }

  /**
   * Turn the received insert video event into an event to the app
   *
   * In useLivefireApi, we add a listener for this event so that it can handle
   * getting that to outlook.
   */
  async insertVideo(item: NewItem) {
    if (!this.authApi) {
      console.warn('Could not retrieve video meta data because API service is not present');
      return;
    }

    try {
      const result = await this.authApi.get(
        new URL(`/app/api/api.php?method=GetVideoMetaData&videoId=${item.id}`, EnvironmentHelper.getAppUrl()),
      );
      if (!result) {
        throw new Error('Could not get metadata');
      }
      const data: VideoMetadataResponse = await result.json();
      this.debug('Received metadata', data);
      if (data.status !== 'success') {
        throw new Error(`Error retrieving metadata: ${data.status}`);
      }
      const event = new CustomEvent<VideoMetadata>('insertVideo', { detail: data.info });
      this.emitter.dispatchEvent(event);
    } catch (err) {
      console.error(err);
    }
  }

  async videoSaved() {
    const event = new CustomEvent('videoSaved');
    this.emitter.dispatchEvent(event);
  }

  onNewItem(event: Event) {
    const { item } = (event as CustomEvent).detail;
    if (item.itemType !== 'insert_video' && item.itemType !== 'video_saved') {
      return super.onNewItem(event);
    }

    if (item.itemType === 'video_saved') {
      this.debug('Received video_saved item', item);
      this.videoSaved();
      return;
    }
    this.debug('Received insert_video item', item);
    this.insertVideo(item);
  }

  setApiService(api: AuthenticatedApiService | null) {
    this.authApi = api;
  }
}
